import React, {useEffect, useState} from "react";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {SelectButton} from "primereact/selectbutton";
import {Checkbox} from "primereact/checkbox";

import {AppContext} from "../../HomePage";
import {Loading} from "../../utils/InlineComponents";
import {ShopifyProductService} from "../../service/ShopifyProductService";

const service = new ShopifyProductService();

export default function ShopifyProductDialog({visible, campaignId, onHide, onSaved}) {
    const {t, setError} = React.useContext(AppContext);
    const [query, setQuery] = useState('');
    const [products, setProducts] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState('all'); // 'all' | 'first'
    // pending changes keyed by variant_id -> true (track) / false (untrack)
    const [changes, setChanges] = useState({});
    // expanded product ids (variant sub-list visible) in 'all' mode
    const [expanded, setExpanded] = useState({});
    // Set when the backend reports a Shopify connection problem:
    // 'shopify_unauthorized' (token revoked) or 'shopify_shop_unavailable'
    // (store closed/frozen/deactivated).
    const [connectionError, setConnectionError] = useState(null);
    // {limit, active, available, requested} when the backend refused a save
    // that would go past the plan's (account-wide) product limit.
    const [limitError, setLimitError] = useState(null);

    const handleError = (error, stopLoading) => {
        const data = error?.response?.data;
        const code = data?.error;
        if (code === 'shopify_unauthorized' || code === 'shopify_shop_unavailable')
            setConnectionError(code);
        else if (code === 'product_limit_exceeded')
            setLimitError(data);
        else {
            setError(error); setError(null);
        }
        stopLoading();
    };
    const toggleExpand = (pid) => setExpanded(prev => ({...prev, [pid]: !prev[pid]}));
    const toggleVariant = (variant, checked) =>
        setChanges(prev => ({...prev, [variant.variant_id]: checked}));

    const search = (q) => {
        if (!campaignId) return;
        setLoading(true);
        service.listProducts(campaignId, q, null).then(res => {
            setProducts((res && res.products) || []);
            setNextCursor((res && res.next_cursor) || null);
            setLoading(false);
        }).catch(error => handleError(error, () => setLoading(false)));
    };

    const loadMore = () => {
        if (!campaignId || !nextCursor || loadingMore) return;
        setLoadingMore(true);
        service.listProducts(campaignId, query, nextCursor).then(res => {
            setProducts(prev => prev.concat((res && res.products) || []));
            setNextCursor((res && res.next_cursor) || null);
            setLoadingMore(false);
        }).catch(error => handleError(error, () => setLoadingMore(false)));
    };

    useEffect(() => {
        if (visible) { setChanges({}); setExpanded({}); setQuery(''); setConnectionError(null); setLimitError(null); search(''); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, campaignId]);

    // Effective tracked state for a variant = pending change if present, else its status.
    const isChecked = (variant) => {
        if (variant.variant_id in changes) return changes[variant.variant_id];
        return variant.status === 'tracked';
    };

    const toggleProduct = (product, checked) => {
        // 'first' mode toggles only the first variant; 'all' toggles every variant.
        const targets = mode === 'first' ? product.variants.slice(0, 1) : product.variants;
        const next = {...changes};
        targets.forEach(v => { next[v.variant_id] = checked; });
        setChanges(next);
    };

    const productChecked = (product) => {
        const targets = mode === 'first' ? product.variants.slice(0, 1) : product.variants;
        return targets.length > 0 && targets.every(isChecked);
    };

    const save = () => {
        const activate = [];
        const deactivate = [];
        Object.keys(changes).forEach(vid => {
            // Only send genuine changes vs current status.
            const product = products.find(p => p.variants.some(v => v.variant_id === vid));
            const variant = product && product.variants.find(v => v.variant_id === vid);
            const wasTracked = variant ? variant.status === 'tracked' : false;
            if (changes[vid] && !wasTracked) activate.push(vid);
            if (!changes[vid] && wasTracked) deactivate.push(vid);
        });
        if (activate.length === 0 && deactivate.length === 0) { onHide(); return; }
        setSaving(true); setLimitError(null);
        service.updateTracking(campaignId, activate, deactivate, mode).then(result => {
            setSaving(false);
            if (onSaved) onSaved(result);
            onHide();
        }).catch(error => handleError(error, () => setSaving(false)));
    };

    const statusBadge = (status) => {
        const map = {
            tracked: {label: t('shopify.status.tracked', 'Tracked'), color: '#16a34a'},
            passive: {label: t('shopify.status.passive', 'Passive'), color: '#d97706'},
            none: {label: t('shopify.status.none', 'Not tracked'), color: '#9ca3af'},
        };
        const s = map[status] || map.none;
        return <span style={{color: s.color, fontSize: 12, fontWeight: 600}}>{s.label}</span>;
    };

    const footer = (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <SelectButton value={mode} onChange={(e) => e.value && setMode(e.value)}
                options={[
                    {label: t('shopify.mode.first', 'First variant only'), value: 'first'},
                    {label: t('shopify.mode.all', 'All variants'), value: 'all'},
                ]}/>
            <span>
                <Button label={t('actions.cancel', 'Cancel')} className="p-button-text" onClick={onHide} disabled={saving}/>
                <Button label={t('actions.save', 'Save')} icon="pi pi-check" onClick={save} loading={saving}/>
            </span>
        </div>
    );

    return (
        <Dialog header={t('shopify.dialog.header', 'Track products from your Shopify store')}
                visible={visible} onHide={onHide} footer={footer} modal
                style={{width: '46em', maxWidth: '95vw'}}>
            <div style={{display: 'flex', gap: 8, marginBottom: 12}}>
                <InputText value={query} onChange={(e) => setQuery(e.target.value)}
                           onKeyDown={(e) => e.key === 'Enter' && search(query)}
                           placeholder={t('shopify.dialog.search', 'Search products')} style={{flex: 1}}/>
                <Button icon="pi pi-search" onClick={() => search(query)}/>
            </div>
            {limitError &&
                <div style={{display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 12,
                             color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8}}>
                    <i className="pi pi-times-circle" style={{fontSize: 18}}/>
                    <span>{t('shopify.dialog.limit_exceeded', limitError)}</span>
                </div>}
            {connectionError ?
                <div style={{display: 'flex', alignItems: 'center', gap: 10, padding: '16px 8px', color: '#b91c1c'}}>
                    <i className="pi pi-exclamation-triangle" style={{fontSize: 20}}/>
                    <span>{connectionError === 'shopify_shop_unavailable'
                        ? t('shopify.dialog.shop_unavailable', 'This Shopify store is no longer reachable (it may be closed, frozen or deactivated), so its products cannot be listed.')
                        : t('shopify.dialog.unauthorized', 'Your Shopify connection is no longer valid. Please open the PriceRest app from your Shopify admin (or reinstall it) to reconnect, then try again.')}</span>
                </div> :
            loading ? <div style={{textAlign: 'center'}}><Loading/></div> :
                <div style={{maxHeight: '50vh', overflowY: 'auto'}}>
                    {products.length === 0 ? <div style={{color: '#6b7280'}}>{t('shopify.dialog.empty', 'No products found.')}</div> :
                        products.map(p => {
                            const multi = p.variants.length > 1;
                            const canExpand = mode === 'all' && multi;
                            const open = canExpand && expanded[p.product_id];
                            return (
                                <div key={p.product_id} style={{borderBottom: '1px solid #eceff1'}}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px'}}>
                                        <Checkbox checked={productChecked(p)} onChange={(e) => toggleProduct(p, e.checked)}/>
                                        {p.image ? <img src={p.image} alt="" style={{width: 36, height: 36, objectFit: 'contain'}}/> : null}
                                        <span style={{flex: 1, cursor: canExpand ? 'pointer' : 'default'}}
                                              onClick={() => canExpand && toggleExpand(p.product_id)}>
                                            {p.title}
                                            {canExpand ? <span style={{color: '#9ca3af', fontSize: 12}}> ({p.variants.length} variants)</span> : null}
                                        </span>
                                        {canExpand ? <i className={open ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
                                                        style={{cursor: 'pointer', color: '#6b7280'}}
                                                        onClick={() => toggleExpand(p.product_id)}/> : null}
                                        {statusBadge(p.status)}
                                    </div>
                                    {open ? p.variants.map(v => (
                                        <div key={v.variant_id}
                                             style={{display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px 6px 44px', borderTop: '1px solid #f4f6f8'}}>
                                            <Checkbox checked={isChecked(v)} onChange={(e) => toggleVariant(v, e.checked)}/>
                                            <span style={{flex: 1, fontSize: 13}}>{v.title || 'Default'}</span>
                                            {v.price ? <span style={{fontSize: 12, color: '#6b7280'}}>{v.price}</span> : null}
                                            {statusBadge(v.status)}
                                        </div>
                                    )) : null}
                                </div>
                            );
                        })}
                    {nextCursor ? (
                        <div style={{textAlign: 'center', padding: '12px 4px'}}>
                            <Button label={t('actions.load_more', 'Load more')}
                                    className="p-button-text"
                                    icon="pi pi-angle-down"
                                    loading={loadingMore}
                                    onClick={loadMore}/>
                        </div>
                    ) : null}
                </div>}
        </Dialog>
    );
}
